import { Component } from '@theme/component';

/**
 * @typedef {Object} Slide
 * @property {string} title
 * @property {string} vendor
 * @property {string} price
 * @property {string|null} comparePrice
 * @property {string} url
 * @property {string} learnUrl
 * @property {string|null} thumbSrc
 * @property {string} thumbAlt
 * @property {string} thumbBg
 * @property {boolean} hasProduct
 * @property {string} bgLink
 */

/**
 * @typedef {Object} SfHeroRotatingGalleryRefs
 * @property {HTMLElement} gallery
 * @property {HTMLButtonElement[]} thumbs
 * @property {HTMLElement} [productCard]
 * @property {HTMLElement} [cardThumbWrap]
 * @property {HTMLElement} [cardVendor]
 * @property {HTMLElement} [cardName]
 * @property {HTMLElement} [cardCompare]
 * @property {HTMLElement} [cardPrice]
 * @property {HTMLAnchorElement} [cardShopLink]
 * @property {HTMLAnchorElement} [cardLearnLink]
 * @property {HTMLAnchorElement} [galleryLink]
 */

/**
 * Hero split gallery — crossfades stacked <img> elements by toggling
 * .hero-split__gallery-img--active, and syncs the floating product card
 * with the active slide using data from a <script type="application/json"
 * data-slides> island.
 *
 * @extends {Component<SfHeroRotatingGalleryRefs>}
 */
class SfHeroRotatingGallery extends Component {
  /** @type {Slide[]} */
  #slides = [];

  connectedCallback() {
    super.connectedCallback();
    this.#slides = this.#parseSlides();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  #parseSlides() {
    const script = this.querySelector('script[type="application/json"][data-slides]');
    if (!script) return [];
    try {
      const data = JSON.parse(script.textContent ?? '[]');
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }

  // Called by on:click="/handleThumbClick" in the template
  handleThumbClick(event) {
    const thumb = /** @type {HTMLButtonElement} */ (event.target.closest('[data-gallery-index]'));
    if (!thumb) return;
    const index = thumb.dataset.galleryIndex;
    if (!index) return;

    if (this.refs.gallery) {
      const images = this.refs.gallery.querySelectorAll('.hero-split__gallery-img');
      for (const img of images) {
        const isActive = img.dataset.galleryIndex === index;
        img.classList.toggle('hero-split__gallery-img--active', isActive);
      }
    }

    for (const t of this.refs.thumbs ?? []) {
      const isActive = t === thumb;
      t.classList.toggle('hero-split__thumb--active', isActive);
      t.setAttribute('aria-pressed', String(isActive));
    }

    this.#updateCard(Number(index));
  }

  #updateCard(index) {
    const slide = this.#slides[index - 1];
    if (!slide) return;

    if (slide.thumbBg) {
      // Set on the component root (.hero-split__inner) so the variable cascades
      // to both the gallery backdrop and the card's thumb-wrap.
      this.style.setProperty('--hero-split-product-thumb-bg', slide.thumbBg);
    }

    // Sync the wrapping <a> on the gallery so clicking the background image
    // takes the user to the slide's configured link.
    if (this.refs.galleryLink) {
      const href = slide.bgLink || slide.url || '';
      if (href) {
        this.refs.galleryLink.href = href;
        this.refs.galleryLink.removeAttribute('aria-disabled');
        this.refs.galleryLink.removeAttribute('tabindex');
      } else {
        this.refs.galleryLink.removeAttribute('href');
        this.refs.galleryLink.setAttribute('aria-disabled', 'true');
        this.refs.galleryLink.setAttribute('tabindex', '-1');
      }
    }

    if (!this.refs.productCard) return;

    if (!slide.hasProduct) {
      this.refs.productCard.hidden = true;
      return;
    }
    this.refs.productCard.hidden = false;

    if (this.refs.cardVendor) this.refs.cardVendor.textContent = slide.vendor;
    if (this.refs.cardName) this.refs.cardName.textContent = slide.title;
    if (this.refs.cardPrice) this.refs.cardPrice.textContent = slide.price;

    if (this.refs.cardCompare) {
      if (slide.comparePrice) {
        this.refs.cardCompare.textContent = slide.comparePrice;
        this.refs.cardCompare.hidden = false;
      } else {
        this.refs.cardCompare.textContent = '';
        this.refs.cardCompare.hidden = true;
      }
    }

    if (this.refs.cardShopLink) this.refs.cardShopLink.href = slide.url;
    if (this.refs.cardLearnLink) this.refs.cardLearnLink.href = slide.learnUrl;

    if (this.refs.cardThumbWrap && slide.thumbSrc) {
      let img = this.refs.cardThumbWrap.querySelector('img');
      if (!img) {
        // Initial render used a placeholder SVG — replace it with a real <img>.
        this.refs.cardThumbWrap.replaceChildren();
        img = document.createElement('img');
        img.className = 'hero-split__product-thumb';
        img.width = 60;
        img.height = 85;
        img.loading = 'lazy';
        this.refs.cardThumbWrap.appendChild(img);
      }
      // Shopify's image_tag emits srcset/sizes — clear them so the new src wins.
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = slide.thumbSrc;
      img.alt = slide.thumbAlt ?? '';
    }
  }
}

if (!customElements.get('sf-hero-rotating-gallery')) {
  customElements.define('sf-hero-rotating-gallery', SfHeroRotatingGallery);
}
